import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BarList,
  Button,
  Card,
  ChartCard,
  EmptyState,
  KpiTile,
  RangePicker,
} from '@/components';
import type { Range, WebAnalyticsResponse } from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

const WEB_HINTS = {
  kpiDau:
    'Unique Amplitude users on the latest Pacific day who fired any web event (platform=web). Delta is versus the prior day.',
  kpiVisitors:
    'Unique Amplitude users in the selected window who fired any Hmonglus web event.',
  kpiLanding: 'Unique users who viewed the public landing page in the window.',
  kpiApp: 'Unique users who opened the learner web app (/app) in the window.',
  kpiCta: 'Landing CTA taps in the window (Start Learning and similar).',
  kpiCheckout: 'Stripe checkout-success events from the web paywall.',
  chartDau:
    'Daily unique web users (Amplitude platform=web). Same Pacific calendar days as the range picker.',
  chartLandingApp:
    'Daily unique landing-page visitors versus unique learner-app visitors.',
  funnel:
    'Ordered unique-user funnel from Amplitude: landing → CTA → web app → paywall. Stripe checkout is a KPI until that event exists in Amplitude.',
  events: 'Unique web users per screen or feature in the selected window.',
} as const;

type WebSearch = { range: Range };

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function formatStepRate(count: number, topCount: number): string {
  if (topCount <= 0) return '—';
  const pct = (count / topCount) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

export function WebAnalyticsPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/web' });
  const qc = useQueryClient();

  const webQ = useQuery({
    queryKey: ['admin', 'web', range],
    queryFn: () =>
      apiJson<WebAnalyticsResponse>(
        `/v1/admin/analytics/web?range=${encodeURIComponent(range)}`
      ),
  });

  const data = webQ.data;
  const loading = webQ.isLoading;

  const chartRows = useMemo(
    () =>
      data?.days.map((d) => ({
        ...d,
        label: fmtDay(d.date),
      })) ?? [],
    [data]
  );

  const sparkDau = data?.days.map((d) => d.dau) ?? [];
  const sparkLanding = data?.days.map((d) => d.landing) ?? [];
  const sparkApp = data?.days.map((d) => d.app) ?? [];
  const hasChartData = chartRows.some((d) => d.dau > 0 || d.landing > 0 || d.app > 0);
  const funnelTop = data?.funnel[0]?.count ?? 0;
  const hasFunnel = (data?.funnel ?? []).some((step) => step.count > 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Web</h1>
          <p className="mt-1 text-sm text-gray-500">
            Hmonglus web app from Amplitude — landing, learner app, and checkout (Pacific
            calendar days).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            options={['7d', '30d', '90d']}
            onChange={(r) => {
              void navigate({ search: { range: r } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'web'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {webQ.error && (
        <p className="text-sm text-red-600">
          {(webQ.error as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void webQ.refetch()}
          >
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiTile
          label="Web DAU"
          value={data?.dau ?? 0}
          delta={data?.delta_dau}
          spark={sparkDau.slice(-14)}
          loading={loading}
          tooltip={WEB_HINTS.kpiDau}
        />
        <KpiTile
          label="Visitors"
          value={data?.visitors ?? 0}
          spark={sparkDau.slice(-14)}
          loading={loading}
          tooltip={WEB_HINTS.kpiVisitors}
        />
        <KpiTile
          label="Landing visitors"
          value={data?.landing_visitors ?? 0}
          spark={sparkLanding.slice(-14)}
          loading={loading}
          tooltip={WEB_HINTS.kpiLanding}
        />
        <KpiTile
          label="App visitors"
          value={data?.app_visitors ?? 0}
          spark={sparkApp.slice(-14)}
          loading={loading}
          tooltip={WEB_HINTS.kpiApp}
        />
        <KpiTile
          label="Landing CTA taps"
          value={data?.cta_presses ?? 0}
          loading={loading}
          tooltip={WEB_HINTS.kpiCta}
        />
        <KpiTile
          label="Checkouts"
          value={data?.checkouts ?? 0}
          loading={loading}
          tooltip={WEB_HINTS.kpiCheckout}
        />
      </div>

      <ChartCard
        title="Daily active web users"
        tooltip={WEB_HINTS.chartDau}
        loading={loading}
        error={webQ.error ? String(webQ.error) : undefined}
        height={280}
      >
        {!hasChartData ? (
          <EmptyState
            title="No web Amplitude events in window"
            hint="Events start after the production Amplitude key was deployed."
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <Tooltip formatter={chartTooltipFormatter} />
              <Line
                type="monotone"
                dataKey="dau"
                name="DAU"
                stroke={PRIMARY_HEX}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Landing vs app visitors"
        tooltip={WEB_HINTS.chartLandingApp}
        loading={loading}
        height={280}
      >
        {!hasChartData ? (
          <EmptyState title="No landing or app visits in window" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <Tooltip formatter={chartTooltipFormatter} />
              <Legend />
              <Line
                type="monotone"
                dataKey="landing"
                name="Landing"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="app"
                name="Web app"
                stroke={PRIMARY_HEX}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Web acquisition funnel" tooltip={WEB_HINTS.funnel} loading={loading} height={300}>
          {!hasFunnel ? (
            <EmptyState title="No funnel data in window" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data?.funnel.map((step) => ({
                  ...step,
                  conversion: formatStepRate(step.count, funnelTop),
                }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                <YAxis
                  allowDecimals={false}
                  width={44}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatInt(Number(v))}
                />
                <Tooltip
                  formatter={(_value, _name, item) => {
                    const row = item?.payload as { count: number; conversion: string };
                    return [`${formatInt(row.count)} (${row.conversion})`, 'Users'];
                  }}
                />
                <Bar dataKey="count" name="Users" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <Card padding="md" className="cursor-help" title={WEB_HINTS.events}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Unique users by surface</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : (
            <BarList
              data={(data?.events ?? []).map((event) => ({
                key: event.key,
                count: event.count,
                label: event.label,
              }))}
              emptyLabel="No web screen events in window"
            />
          )}
          {!loading && (data?.store_presses ?? 0) > 0 ? (
            <p className="mt-4 text-xs text-gray-500">
              Store badge taps: {formatInt(data?.store_presses ?? 0)}
            </p>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/web')({
  validateSearch: (raw: Record<string, unknown>): WebSearch => {
    const r = raw.range;
    if (r === '7d' || r === '30d' || r === '90d') return { range: r };
    return { range: '30d' };
  },
  component: WebAnalyticsPage,
});
